import type { ChatThread } from "@/redux/reducers/chatSlice";
import {
  getMeetingIdsForStoredSession,
  persistSessionMeetingEntry,
  type SessionMeetingMapEntry,
} from "@/utils/sessionMeetingMap";

function normalizeMeetingIdsList(ids: unknown[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of ids) {
    const s = String(x).trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

/** Parse meeting / Teams metadata from a GET /api/sessions row (handles alternate API shapes). */
export function parseSessionRowMeetingMetadata(session: Record<string, unknown>): {
  meeting_id?: string;
  meeting_ids?: string[];
  type?: string;
  source?: string;
  chatType?: string;
  meeting_name?: string;
} {
  const meta =
    session.metadata && typeof session.metadata === "object" && !Array.isArray(session.metadata)
      ? (session.metadata as Record<string, unknown>)
      : null;
  const ctx =
    session.context && typeof session.context === "object" && !Array.isArray(session.context)
      ? (session.context as Record<string, unknown>)
      : null;

  const meetingIdsRaw =
    (Array.isArray(session.meeting_ids) && session.meeting_ids) ||
    (Array.isArray(session.meetingIds) && session.meetingIds) ||
    (Array.isArray(meta?.meeting_ids) && meta.meeting_ids) ||
    (Array.isArray(ctx?.meeting_ids) && ctx.meeting_ids) ||
    [];

  const meeting_ids = normalizeMeetingIdsList(meetingIdsRaw as unknown[]);

  const meetingIdRaw =
    session.meeting_id ??
    session.meetingId ??
    meta?.meeting_id ??
    meta?.meetingId ??
    ctx?.meeting_id ??
    ctx?.meetingId;

  const meeting_id =
    meetingIdRaw != null && String(meetingIdRaw).trim() !== ""
      ? String(meetingIdRaw).trim()
      : meeting_ids[0];

  const typeRaw =
    (typeof session.type === "string" && session.type) ||
    (typeof session.session_type === "string" && session.session_type) ||
    (typeof meta?.type === "string" && meta.type) ||
    undefined;

  const sourceRaw =
    (typeof session.source === "string" && session.source) ||
    (typeof meta?.source === "string" && meta.source) ||
    (typeof ctx?.source === "string" && ctx.source) ||
    undefined;

  const chatTypeRaw =
    (typeof session.chat_type === "string" && session.chat_type) ||
    (typeof session.chatType === "string" && session.chatType) ||
    (typeof meta?.chat_type === "string" && meta.chat_type) ||
    undefined;

  const type = typeRaw?.trim() || undefined;
  const source = sourceRaw?.trim() || undefined;
  let chatType = chatTypeRaw?.trim() || undefined;

  if (
    !chatType &&
    (type === "meeting_notes" ||
      type === "meeting_notes_chat" ||
      source === "teams_ai_assist")
  ) {
    chatType = "teams_ai_assist";
  }

  const meeting_name =
    typeof session.meeting_name === "string" && session.meeting_name.trim()
      ? session.meeting_name.trim()
      : typeof meta?.meeting_name === "string" && meta.meeting_name.trim()
        ? meta.meeting_name.trim()
        : undefined;

  return {
    ...(meeting_id && { meeting_id }),
    ...(meeting_ids.length > 0 && { meeting_ids }),
    ...(type && { type }),
    ...(source && { source }),
    ...(chatType && { chatType }),
    ...(meeting_name && { meeting_name }),
  };
}

export function deriveMeetingIdsFromThread(
  thread: ChatThread | null | undefined
): string[] {
  if (!thread) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (id: string) => {
    const t = id.trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    out.push(t);
  };
  if (Array.isArray(thread.meeting_ids)) {
    for (const m of thread.meeting_ids) push(String(m));
  }
  if (thread.meeting_id != null) push(String(thread.meeting_id));
  return out;
}

export function isMeetingAssistThread(
  thread: ChatThread | null | undefined
): boolean {
  if (!thread) return false;
  if (deriveMeetingIdsFromThread(thread).length > 0) return true;
  if (thread.chatType === "teams_ai_assist" || thread.source === "teams_ai_assist") {
    return true;
  }
  const t = thread.type;
  return t === "meeting_notes" || t === "meeting_notes_chat";
}

/** Collect unique meeting ids from all thread rows (for bounded history probe). */
export function collectMeetingIdsFromThreads(
  threads: Record<string, ChatThread>
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of Object.values(threads)) {
    for (const id of deriveMeetingIdsFromThread(t)) {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
  }
  return out;
}

export function resolveMeetingIdsForSession(
  threads: Record<string, ChatThread>,
  sessionId: string
): string[] {
  const trimmed = sessionId.trim();
  if (!trimmed) return [];

  const lower = trimmed.toLowerCase();
  let thread: ChatThread | undefined = threads[trimmed];
  if (!thread) {
    for (const t of Object.values(threads)) {
      const sid = t?.session_id;
      if (sid != null && String(sid).trim().toLowerCase() === lower) {
        thread = t;
        break;
      }
    }
  }

  const fromThread = deriveMeetingIdsFromThread(thread);
  if (fromThread.length > 0) return fromThread;

  const fromStorage = getMeetingIdsForStoredSession(trimmed);
  if (fromStorage.length > 0) return fromStorage;

  return [];
}

export function persistThreadMeetingLink(
  sessionId: string,
  thread: ChatThread | null | undefined
): void {
  if (!thread) return;
  const ids = deriveMeetingIdsFromThread(thread);
  if (ids.length === 0 && !isMeetingAssistThread(thread)) return;
  const entry: Partial<SessionMeetingMapEntry> = {
    ...(ids[0] && { meeting_id: ids[0] }),
    ...(ids.length > 0 && { meeting_ids: ids }),
    ...(thread.type && { type: thread.type }),
    ...(thread.source && { source: thread.source }),
    ...(thread.chatType && { chatType: thread.chatType }),
  };
  persistSessionMeetingEntry(sessionId, entry);
}
